import { type SubbookId } from "@/program/Card";

const SUBBOOK_OPTIONS: { id: SubbookId; label: string }[] = [
  { id: "subbook-a", label: "サブブック A" },
  { id: "subbook-b", label: "サブブック B" },
  { id: "subbook-c", label: "サブブック C" },
];

/**
 * callカードが呼び出すサブブックを選択するモーダル
 */
export class SubbookSelectorModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private onSelectCallback?: (subbookId: SubbookId) => void;
  private onCloseCallback?: () => void;
  private isOpen: boolean = false;
  private previousFocus: HTMLElement | null = null;
  private slots: number = 3;

  constructor() {
    // オーバーレイ
    this.overlay = document.createElement("div");
    this.overlay.className = "subbook-selector-modal-overlay";
    this.overlay.addEventListener("click", () => this.close());

    // モーダル本体
    this.modal = document.createElement("div");
    this.modal.className = "subbook-selector-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-labelledby", "subbook-selector-title");
    this.modal.addEventListener("click", (e) => e.stopPropagation());

    this.overlay.appendChild(this.modal);
  }

  /**
   * モーダルを開く
   * @param slots 表示するサブブックスロット数（省略時は3）
   */
  open(slots: number = 3): void {
    this.slots = slots;
    if (this.isOpen) return;
    this.isOpen = true;

    this.previousFocus = document.activeElement as HTMLElement | null;
    this.render();
    document.body.appendChild(this.overlay);

    document.addEventListener("keydown", this.handleKeyDown);

    requestAnimationFrame(() => {
      const first = this.modal.querySelector("button:not([disabled])");
      if (first instanceof HTMLElement) first.focus();
    });
  }

  /**
   * モーダルを閉じる
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.remove();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.previousFocus?.focus();
    this.onCloseCallback?.();
  }

  /**
   * モーダルが開いているか
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }

  /**
   * 選択時コールバック
   */
  onSelect(callback: (subbookId: SubbookId) => void): void {
    this.onSelectCallback = callback;
  }

  /**
   * 閉じた時コールバック
   */
  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "Tab") {
      this.trapFocus(e);
    }
  };

  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.modal.querySelectorAll("button:not([disabled])");
    if (focusable.length === 0) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private render(): void {
    const children: HTMLElement[] = [];

    const title = document.createElement("h2");
    title.id = "subbook-selector-title";
    title.className = "subbook-selector-modal__title";
    title.textContent = "よびだすサブブックを選択";
    children.push(title);

    const grid = document.createElement("div");
    grid.className = "subbook-selector-modal__grid";

    for (const option of SUBBOOK_OPTIONS.slice(0, this.slots)) {
      const button = document.createElement("button");
      button.className = "subbook-selector-modal__option";
      button.textContent = option.label;
      button.addEventListener("click", () => {
        this.onSelectCallback?.(option.id);
        this.close();
      });
      grid.appendChild(button);
    }

    children.push(grid);

    const closeBtn = document.createElement("button");
    closeBtn.className = "subbook-selector-modal__close";
    closeBtn.textContent = "キャンセル";
    closeBtn.addEventListener("click", () => this.close());
    children.push(closeBtn);

    this.modal.replaceChildren(...children);
  }
}
